import * as styles from "./ContactList.css";

import { useAtomValue } from "jotai";
import { UserPlus } from "lucide-react";
import { useMemo, useRef } from "react";

import { Button } from "@frontend/components/atoms/Button";
import {
  ScrollArea,
  ScrollAreaContent,
  ScrollAreaScrollbar,
  ScrollAreaThumb,
  ScrollAreaViewport,
} from "@frontend/components/atoms/ScrollArea";
import { useVirtualizer } from "@tanstack/react-virtual";

import { useAddressBookActions } from "../../hooks/useAddressBookActions";
import { useContactListScroll } from "../../hooks/useContactListScroll";
import { useContactSections } from "../../hooks/useContactSections";
import { filteredContactsAtom, paneAtom } from "../../stores";
import type { FlatItem } from "../../types";
import { CategorySelect } from "../CategorySelect";
import { Panel, PanelFooter } from "../Panel";
import { Search, SearchFilter } from "../Search";
import { AlphaIndex } from "./AlphaIndex";
import { ListItem } from "./ListItem";
import { ListSection } from "./ListSection";
import { StickyHeader } from "./StickyHeader";
import { LIST_ITEM_HEIGHT, LIST_SECTION_HEIGHT } from "./constants";

export const ContactList = () => {
  // usePrefetchAvatars(contacts);

  const { selectContact, newContact } = useAddressBookActions();

  const contacts = useAtomValue(filteredContactsAtom);

  const pane = useAtomValue(paneAtom);

  const sections = useContactSections(contacts);

  const scrollRef = useRef<HTMLDivElement>(null);

  const flatItems = useMemo<FlatItem[]>(
    () =>
      Object.entries(sections).flatMap(([letter, contacts]) => [
        { type: "section", letter },
        ...contacts.map((contact) => ({ type: "item" as const, contact })),
      ]),
    [sections],
  );

  const virtualizer = useVirtualizer({
    count: flatItems.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (i) =>
      flatItems[i].type === "section" ? LIST_SECTION_HEIGHT : LIST_ITEM_HEIGHT,
    overscan: 5,
    getItemKey: (i) => {
      const item = flatItems[i];

      return item.type === "section"
        ? `section-${item.letter}`
        : `contact-${item.contact.id}`;
    },
  });

  useContactListScroll({ scrollRef, flatItems, virtualizer });

  const currentSectionLetter = useMemo(() => {
    const startIndex = virtualizer.range?.startIndex ?? 0;

    for (let i = startIndex; i >= 0; i--) {
      const item = flatItems[i];

      if (item?.type === "section") {
        return item.letter;
      }
    }

    return null;
  }, [virtualizer.range, flatItems]);

  return (
    <Panel hiddenBelow={pane === "list" ? "none" : "mobile"}>
      <div className={styles.header}>
        <Search rightSection={<SearchFilter />} />

        <div className={styles.categorySelect}>
          <CategorySelect />
        </div>
      </div>

      <div className={styles.content}>
        <ScrollArea className={styles.scrollArea}>
          <ScrollAreaViewport ref={scrollRef}>
            <ScrollAreaContent>
              <StickyHeader letter={currentSectionLetter} />

              <div
                className={styles.inner}
                style={{ height: virtualizer.getTotalSize() }}
              >
                {virtualizer.getVirtualItems().map((virtualItem) => {
                  const item = flatItems[virtualItem.index];

                  return (
                    <div
                      key={virtualItem.key}
                      className={styles.items}
                      style={{
                        transform: `translateY(${virtualItem.start}px)`,
                      }}
                    >
                      {item.type === "section" ? (
                        <ListSection letter={item.letter} />
                      ) : (
                        <ListItem
                          contact={item.contact}
                          onSelect={selectContact}
                          isLast={virtualItem.index === flatItems.length - 1}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </ScrollAreaContent>
          </ScrollAreaViewport>

          <ScrollAreaScrollbar alwaysVisible expandOnHover>
            <ScrollAreaThumb />
          </ScrollAreaScrollbar>
        </ScrollArea>

        <AlphaIndex />
      </div>

      <PanelFooter className={styles.footer}>
        <Button onClick={() => newContact()}>
          <UserPlus size={16} />
        </Button>
      </PanelFooter>
    </Panel>
  );
};
